import {
  Apartment,
  SendOutlined,
  AssignmentOutlined,
  BarChartOutlined,
  HomeOutlined,
  AccountCircleOutlined,
  PeopleOutlined,
  Security,
  TableChartOutlined,
  HelpOutline,
  Login as Logout,
} from '@mui/icons-material';
import { externalLinks, externalTitles, routes } from '@/constants/Constants';
import { useLocation } from 'react-router';
import { useGoToExternal, useNavigateWithSegment } from '@/utils/linkUtils';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import useProfileDrawerStore from '@/stores/useProfileDrawerStore';
import { useQuery } from '@tanstack/react-query';
import getUser from '@/utils/apiRestCalls/getUser';
import logout from '@/utils/axios/logout';
import { shouldDisableRenterInvite, shouldUserStayInEnrollProperty } from '@/utils/commonUtils';
import { useUrlChecker } from '@/hooks/useUrlChecker';
import { ZgriVisibilityGuard } from '@/features/RentersInsurance/guards';
import { RiNewBadge } from '@/features/RentersInsurance/components';
import { CoreVisibilityGuard } from '@/components/guards';
import { UserPermissions } from '@/constants/permissions.constant';
import { usePermission } from '@/hooks/usePermission';
import { useAnalyticsContext } from '@/utils/analytics/AnalyticsContext';
import SideMenuItemV2 from '@/appWrapper/SideMenuItem/V2';
import SideMenuDividerV2 from '@/appWrapper/SideMenuDivider/V2';
import { useFeatureFlag } from '@/hooks';
import { theme } from '@theguarantors/tg-package-material-ui';
import { SegmentObj } from '@/utils/types/LinkTypes.interface';

type Props = {
  menuOpen: boolean;
  closeMenu: () => void;
};

const MenuContent = ({ menuOpen, closeMenu }: Props) => {
  const navigateWithSegment = useNavigateWithSegment();
  const navigate = (url: string, segmentObj?: SegmentObj) => {
    closeMenu();
    navigateWithSegment(url, segmentObj);
  };
  const goToExternal = useGoToExternal();
  const location = useLocation();
  const { showProfileDrawer } = useProfileDrawerStore();
  const { isPublic } = useUrlChecker();
  const { track } = useAnalyticsContext();

  const isSSUMEnabled = useFeatureFlag('otters-3986_self_service_user_management_enabled');

  const { enabled: canManageUsersEnabled } = usePermission(UserPermissions.User.CanManageUsers);

  const { notSet: canEnrollPropertiesNotSet, enabled: canEnrollPropertiesEnabled } = usePermission(
    UserPermissions.Properties.CanEnrollProperty,
  );
  const { notSet: userCanFileClaimNotSet, enabled: userCanFileClaimEnabled } = usePermission(
    UserPermissions.Claim.CanFileClaim,
  );
  const { notSet: userCanInviteRenterNotSet, enabled: userCanInviteRenterEnabled } = usePermission(
    UserPermissions.Invite.CanInviteRenter,
  );

  const canEnrollProperties = canEnrollPropertiesNotSet || canEnrollPropertiesEnabled;
  const userCanInviteRenter = userCanInviteRenterNotSet || userCanInviteRenterEnabled;
  const userCanFileClaim = userCanFileClaimNotSet || userCanFileClaimEnabled;

  const { notSet: userCanAccessDealsDashboardNotSet, enabled: userCanAccessDealsDashboardEnabled } =
    usePermission(UserPermissions.Deal.CanAccessDealsDashboard);

  const userCanAccessDealsDashboard =
    userCanAccessDealsDashboardNotSet || userCanAccessDealsDashboardEnabled;

  const { enabled: userCanViewReportEnabled } = usePermission(UserPermissions.Claim.CanViewReport);

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: () => getUser(),
    staleTime: Infinity,
  });

  const onLogOut = () => {
    track('Logout Button is clicked', {
      title: 'Logout',
      referrer: window.location.pathname,
    });
    logout();
  };

  const onProfile = () => {
    track('Profile Button is clicked', {
      title: 'Profile',
      referrer: window.location.pathname,
    });
    showProfileDrawer();
  };

  const goToFAQ = () => {
    if (user && !canEnrollProperties) {
      return goToExternal(externalLinks.operatorsFAQ, {
        title: 'FAQ Menu (Operator)',
        path: externalLinks.operatorsFAQ,
      });
    }

    return goToExternal(externalLinks.faq, {
      title: 'FAQ Menu',
      path: 'enroll-property#faq',
    });
  };

  const shouldDisableActionIcons = shouldUserStayInEnrollProperty(location.pathname, user);
  const userIsOnHome = location.pathname === routes.home;
  const userIsOnPropertyEnrollment = location.pathname.includes(routes.propertyEnrollmentForm);
  const userIsOnRenterInvite = location.pathname.includes(routes.renterInvite);
  const userIsOnDealsDashboard = location.pathname.includes(routes.dealsDashboard);
  const userIsOnClaimsReport = location.pathname.includes(routes.claimsReport);
  const userIsOnClaimsForm =
    location.pathname.includes(routes.claimsForm) ||
    location.pathname.includes(routes.claimsFormInstructions) ||
    location.pathname.includes(routes.claimsThankYou);

  return (
    <Grid
      container
      sx={{
        height: '100%',
        width: '100%',
        pb: '8px',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
        flexWrap: 'nowrap',
        justifyContent: 'space-between',
        flexDirection: 'column',
        ['&>.MuiGrid-root']: {
          paddingLeft: '4px',
          paddingRight: '4px',
        },
        ['svg']: {
          fill: `${theme.palette.neutralGrey[700]} !important`,
        },
      }}
    >
      {!isPublic && (
        <Grid
          id="firstColumnIcons"
          sx={{
            py: '16px',
          }}
        >
          <SideMenuItemV2
            open={menuOpen}
            text="Home"
            icon={<HomeOutlined />}
            disabled={shouldDisableActionIcons && !userIsOnHome}
            selected={userIsOnHome}
            action={() => navigate(routes.home, { title: 'Visit Home Menu', path: '/' })}
          />
          {isSSUMEnabled && canManageUsersEnabled && (
            <SideMenuItemV2
              open={menuOpen}
              text="User management"
              icon={<PeopleOutlined />}
              action={() => navigate(routes.users)}
            />
          )}
          {canEnrollProperties && (
            <SideMenuItemV2
              open={menuOpen}
              text="Enroll Properties"
              icon={<Apartment />}
              disabled={shouldDisableActionIcons && !userIsOnPropertyEnrollment}
              selected={userIsOnPropertyEnrollment}
              action={() =>
                navigate(routes.propertyEnrollmentForm, {
                  title: 'Visit Enroll Properties Menu',
                  path: '/enroll/property-type',
                })
              }
            />
          )}

          <SideMenuDividerV2 label="Lease Guarantee" open={menuOpen} />

          {user && userCanInviteRenter && (
            <CoreVisibilityGuard>
              <SideMenuItemV2
                icon={<SendOutlined />}
                open={menuOpen}
                text="Invite Renters"
                action={() => navigate(routes.renterInvite)}
                selected={userIsOnRenterInvite}
                disabled={
                  (shouldDisableActionIcons && !userIsOnRenterInvite) ||
                  shouldDisableRenterInvite(user)
                }
              />
            </CoreVisibilityGuard>
          )}

          {user && userCanAccessDealsDashboard && (
            <CoreVisibilityGuard>
              <SideMenuItemV2
                open={menuOpen}
                text={'Application Tracker'}
                icon={<TableChartOutlined />}
                disabled={shouldDisableActionIcons && !userIsOnDealsDashboard}
                selected={userIsOnDealsDashboard}
                action={() => {
                  navigate(routes.dealsDashboard);
                }}
              />
            </CoreVisibilityGuard>
          )}

          {userCanFileClaim && (
            <CoreVisibilityGuard>
              <SideMenuItemV2
                open={menuOpen}
                text={'File a Claim'}
                disabled={shouldDisableActionIcons || !userCanFileClaim}
                icon={<AssignmentOutlined />}
                action={() => navigate(routes.claimsFormInstructions)}
                selected={userIsOnClaimsForm}
              />
            </CoreVisibilityGuard>
          )}

          {userCanViewReportEnabled && (
            <SideMenuItemV2
              open={menuOpen}
              text="Claims Dashboard"
              disabled={shouldDisableActionIcons && !userIsOnClaimsReport}
              icon={<BarChartOutlined />}
              action={() => navigate(routes.claimsReport)}
              selected={userIsOnClaimsReport}
            />
          )}

          <ZgriVisibilityGuard>
            <SideMenuDividerV2 label="Renter Insurance" open={menuOpen} />

            <SideMenuItemV2
              open={menuOpen}
              text="ZGRI Dashboard"
              displayText={
                <Box component={'span'} display={'flex'} alignItems={'center'} columnGap={'8px'}>
                  <Typography sx={{ color: 'inherit' }} component={'span'}>
                    Renters Insurance
                  </Typography>
                  <RiNewBadge />
                </Box>
              }
              icon={<Security />}
              action={() =>
                navigate(routes.rentersInsurance, {
                  title: 'Visit Renters Insurance',
                  path: '/renters-insurance',
                })
              }
              selected={location.pathname.includes(routes.rentersInsurance)}
            />
          </ZgriVisibilityGuard>
        </Grid>
      )}
      <Grid sx={{ flex: 1 }} />
      <Grid
        sx={{
          borderTop: `1px solid ${theme.palette.neutralGrey[200]}`,
          paddingTop: '12px !important',
        }}
      >
        {!isPublic && (
          <SideMenuItemV2
            open={menuOpen}
            text="Profile"
            icon={<AccountCircleOutlined />}
            action={onProfile}
          />
        )}
        <SideMenuItemV2
          open={menuOpen}
          text={externalTitles.faq}
          icon={<HelpOutline />}
          action={goToFAQ}
        />
        <SideMenuItemV2 open={menuOpen} text="Logout" icon={<Logout />} action={onLogOut} />
      </Grid>
    </Grid>
  );
};

export default MenuContent;
